import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { TUploadedFile } from '@onesource/schemas';
import { enqueueSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { MdContentCopy, MdPictureAsPdf, MdZoomIn } from 'react-icons/md';

import { CopyUrlMenu } from './copy-url-menu';
import { MediaPreviewDialog } from './media-preview-dialog';

function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const SKELETON_ROWS = 6;
const COL_SPAN = 7;

function TableSkeleton() {
  return (
    <>
      {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <TableRow key={i}>
          <TableCell sx={{ width: 56, py: 0.75 }}>
            <Skeleton variant="rectangular" width={44} height={44} sx={{ borderRadius: 1 }} />
          </TableCell>
          <TableCell>
            <Skeleton width="60%" />
          </TableCell>
          <TableCell>
            <Skeleton width={80} />
          </TableCell>
          <TableCell>
            <Skeleton width={60} />
          </TableCell>
          <TableCell>
            <Skeleton width={48} />
          </TableCell>
          <TableCell>
            <Skeleton width={80} />
          </TableCell>
          <TableCell align="right">
            <Skeleton width={64} sx={{ ml: 'auto' }} />
          </TableCell>
        </TableRow>
      ))}
    </>
  );
}

export type TMediaTableProps = {
  files: TUploadedFile[];
  isLoading: boolean;
};

export function MediaTable({ files, isLoading }: TMediaTableProps) {
  const [previewFile, setPreviewFile] = useState<TUploadedFile | null>(null);

  const handleCopyUrl = useCallback((url: string) => {
    navigator.clipboard.writeText(url);
    enqueueSnackbar('URL copied to clipboard', { variant: 'success', autoHideDuration: 2000 });
  }, []);

  const handleClosePreview = useCallback(() => setPreviewFile(null), []);
  const handleNavigate = useCallback((file: TUploadedFile) => setPreviewFile(file), []);

  const headers = (
    <TableRow>
      <TableCell>Preview</TableCell>
      <TableCell>Name</TableCell>
      <TableCell>MIME type</TableCell>
      <TableCell>Ext</TableCell>
      <TableCell>Size</TableCell>
      <TableCell>Uploaded</TableCell>
      <TableCell align="right">Actions</TableCell>
    </TableRow>
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>{headers}</TableHead>
          <TableBody>
            {files.length === 0 && !isLoading ? (
              <TableRow>
                <TableCell colSpan={COL_SPAN} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    No files uploaded yet. Click "Upload" to add files.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              files.map(file => {
                const isPdf = file.mimeType === 'application/pdf';
                const ext = file.filename.split('.').pop()?.toLowerCase() ?? '';
                return (
                  <TableRow key={file.key} hover>
                    {/* Thumbnail */}
                    <TableCell sx={{ width: 56, py: 0.75 }}>
                      {isPdf ? (
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: 1,
                            bgcolor: 'action.hover',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <MdPictureAsPdf size={24} color="#e53935" />
                        </Box>
                      ) : file.publicUrl ? (
                        <Box
                          component="img"
                          src={file.publicUrl}
                          alt={file.filename}
                          sx={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 1, display: 'block' }}
                        />
                      ) : (
                        <Box sx={{ width: 44, height: 44, borderRadius: 1, bgcolor: 'action.hover' }} />
                      )}
                    </TableCell>

                    {/* Name */}
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {file.filename}
                      </Typography>
                    </TableCell>

                    {/* MIME type */}
                    <TableCell>
                      <Chip label={file.mimeType} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                    </TableCell>

                    {/* Extension */}
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '0.75rem' }}
                      >
                        {ext}
                      </Typography>
                    </TableCell>

                    {/* Size */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatBytes(file.size)}
                      </Typography>
                    </TableCell>

                    {/* Uploaded */}
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {new Date(file.lastModified).toLocaleDateString()}
                      </Typography>
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="Preview" arrow>
                        <IconButton onClick={() => setPreviewFile(file)}>
                          <MdZoomIn size={20} />
                        </IconButton>
                      </Tooltip>
                      {file.cdnUrls && Object.keys(file.cdnUrls).length > 0 ? (
                        <CopyUrlMenu cdnUrls={file.cdnUrls} variantSizes={{}} standalone iconSize={20} />
                      ) : (
                        file.publicUrl && (
                          <Tooltip title="Copy URL" arrow>
                            <IconButton onClick={() => handleCopyUrl(file.publicUrl!)}>
                              <MdContentCopy size={20} />
                            </IconButton>
                          </Tooltip>
                        )
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Overlay skeleton while loading */}
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            zIndex: 1,
            bgcolor: theme => theme.palette.background.default,
            borderRadius: 1,
          }}
        >
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>{headers}</TableHead>
              <TableBody>
                <TableSkeleton />
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <MediaPreviewDialog file={previewFile} onClose={handleClosePreview} files={files} onNavigate={handleNavigate} />
    </Box>
  );
}
